import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Pill } from '../ui/pill';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Slider } from '../ui/slider';
import { GlassyCard } from '../ui/glassy-card';
import { Separator } from '../ui/separator';
import {
  Database,
  Settings,
  HardDrive,
  Zap,
  AlertTriangle,
  RotateCcw,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { DeviceStorageSettings, useDeviceActions, useDeviceStorageSettings } from '../../stores/deviceStore';

interface DeviceStorageSettingsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  deviceId: string;
  deviceName: string;
}

export function DeviceStorageSettingsModal({
  open,
  onOpenChange,
  deviceId,
  deviceName
}: DeviceStorageSettingsModalProps) {
  const { updateDeviceStorageSettings, resetDeviceStorageSettings } = useDeviceActions();
  const currentSettings = useDeviceStorageSettings(deviceId);

  const [settings, setSettings] = useState<DeviceStorageSettings | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (open && currentSettings) {
      setSettings({ ...currentSettings });
      setHasChanges(false);
    }
  }, [open, currentSettings]);

  const handleSettingChange = <K extends keyof DeviceStorageSettings>(
    key: K,
    value: DeviceStorageSettings[K]
  ) => {
    if (!settings) return;

    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    setHasChanges(true);
  };

  const handleSave = () => {
    if (!settings) return;

    updateDeviceStorageSettings(deviceId, settings);
    setHasChanges(false);
    onOpenChange(false);
  };

  const handleReset = () => {
    resetDeviceStorageSettings(deviceId);
    setHasChanges(false);
    onOpenChange(false);
  };

  const handleCancel = () => {
    setHasChanges(false);
    onOpenChange(false);
  };

  if (!settings) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] sm:max-w-4xl mx-2 max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Database className="w-5 h-5 text-slate-400" strokeWidth={1.5} />
            Storage Settings - {deviceName}
          </DialogTitle>
          <DialogDescription>
            Configure device-specific storage and recording settings
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Recording Settings */}
          <GlassyCard className="p-6 space-y-6">
            <div>
              <h3 className="lb-h3 flex items-center gap-2">
                <Settings className="w-5 h-5 text-slate-400" strokeWidth={1.5} />
                Recording Settings
              </h3>
              <p className="text-sm t-faint mt-1">Basic recording and session configuration</p>
            </div>

            {/* Auto Record */}
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <label className="text-sm font-medium">Auto Record</label>
                <p className="text-xs t-faint">
                  Automatically start recording when device connects
                </p>
              </div>
              <Switch
                checked={settings.autoRecord}
                onCheckedChange={(checked) => handleSettingChange('autoRecord', checked)}
              />
            </div>

            {/* Default Format */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Default Format</label>
              <Select
                value={settings.defaultFormat}
                onValueChange={(value) => handleSettingChange('defaultFormat', value as DeviceStorageSettings['defaultFormat'])}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="jsonl">JSON Lines (.jsonl)</SelectItem>
                  <SelectItem value="csv">CSV (.csv)</SelectItem>
                  <SelectItem value="binary">Binary (.bin)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs t-faint">
                Default file format for recordings from this device
              </p>
            </div>

            {/* Session Name Template */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Session Name Template</label>
              <Input
                value={settings.sessionNameTemplate}
                onChange={(e) => handleSettingChange('sessionNameTemplate', e.target.value)}
                placeholder="{deviceName}_{date}_{time}"
              />
              <p className="text-xs t-faint">
                Template for session names. Use {'{deviceName}'}, {'{date}'}, {'{time}'}
              </p>
            </div>

            {/* Max Session Duration */}
            <div className="space-y-2">
              <label className="text-sm font-medium">
                Max Session Duration: {settings.maxSessionDuration} minutes
              </label>
              <Slider
                value={[settings.maxSessionDuration]}
                onValueChange={(value: number[]) => handleSettingChange('maxSessionDuration', value[0])}
                min={5}
                max={480}
                step={5}
                className="w-full"
              />
              <p className="text-xs t-faint">
                Maximum recording duration before automatic stop
              </p>
            </div>
          </GlassyCard>

          {/* Storage Settings */}
          <GlassyCard className="p-6 space-y-6">
            <div>
              <h3 className="lb-h3 flex items-center gap-2">
                <HardDrive className="w-5 h-5 text-slate-400" strokeWidth={1.5} />
                Storage Settings
              </h3>
              <p className="text-sm t-faint mt-1">File storage and compression configuration</p>
            </div>

            {/* Compression */}
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <label className="text-sm font-medium">Enable Compression</label>
                <p className="text-xs t-faint">
                  Compress data files to save storage space
                </p>
              </div>
              <Switch
                checked={settings.compressionEnabled}
                onCheckedChange={(checked) => handleSettingChange('compressionEnabled', checked)}
              />
            </div>

            {/* Save Location */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Save Location</label>
              <Input
                value={settings.saveLocation}
                onChange={(e) => handleSettingChange('saveLocation', e.target.value)}
                placeholder="sessions"
              />
              <p className="text-xs t-faint">
                Relative path within storage directory for this device
              </p>
            </div>
          </GlassyCard>

          {/* Performance Settings */}
          <GlassyCard className="p-6 space-y-6">
            <div>
              <h3 className="lb-h3 flex items-center gap-2">
                <Zap className="w-5 h-5 text-slate-400" strokeWidth={1.5} />
                Performance Settings
              </h3>
              <p className="text-sm t-faint mt-1">Memory and performance optimization settings</p>
            </div>

            {/* Auto Save Interval */}
            <div className="space-y-2">
              <label className="text-sm font-medium">
                Auto Save Interval: {settings.autoSaveInterval} seconds
              </label>
              <Slider
                value={[settings.autoSaveInterval]}
                onValueChange={(value: number[]) => handleSettingChange('autoSaveInterval', value[0])}
                min={1}
                max={60}
                step={1}
                className="w-full"
              />
              <p className="text-xs t-faint">
                How often to save data during recording
              </p>
            </div>

            {/* Memory Threshold */}
            <div className="space-y-2">
              <label className="text-sm font-medium">
                Memory Threshold: {settings.memoryThreshold} MB
              </label>
              <Slider
                value={[settings.memoryThreshold]}
                onValueChange={(value: number[]) => handleSettingChange('memoryThreshold', value[0])}
                min={10}
                max={200}
                step={10}
                className="w-full"
              />
              <p className="text-xs t-faint">
                Memory usage limit before triggering cleanup
              </p>
            </div>
          </GlassyCard>

          {/* Quality Settings */}
          <GlassyCard className="p-6 space-y-6">
            <div>
              <h3 className="lb-h3 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-slate-400" strokeWidth={1.5} />
                Quality Settings
              </h3>
              <p className="text-sm t-faint mt-1">Signal quality monitoring and control</p>
            </div>

            {/* Min Signal Quality */}
            <div className="space-y-2">
              <label className="text-sm font-medium">
                Minimum Signal Quality: {settings.minSignalQuality}%
              </label>
              <Slider
                value={[settings.minSignalQuality]}
                onValueChange={(value: number[]) => handleSettingChange('minSignalQuality', value[0])}
                min={0}
                max={100}
                step={5}
                className="w-full"
              />
              <p className="text-xs t-faint">
                Minimum acceptable signal quality threshold
              </p>
            </div>

            {/* Pause on Low Quality */}
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <label className="text-sm font-medium">Pause on Low Quality</label>
                <p className="text-xs t-faint">
                  Automatically pause recording when signal quality drops below threshold
                </p>
              </div>
              <Switch
                checked={settings.pauseOnLowQuality}
                onCheckedChange={(checked) => handleSettingChange('pauseOnLowQuality', checked)}
              />
            </div>
          </GlassyCard>

          {/* Current Settings Summary */}
          <GlassyCard className="p-6 space-y-4">
            <div>
              <h3 className="lb-h3 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-slate-400" strokeWidth={1.5} />
                Settings Summary
              </h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span>Auto Record:</span>
                  <Pill size="sm" variant="secondary">
                    {settings.autoRecord ? 'Enabled' : 'Disabled'}
                  </Pill>
                </div>
                <div className="flex justify-between items-center">
                  <span>Format:</span>
                  <Pill size="sm" variant="secondary">{settings.defaultFormat.toUpperCase()}</Pill>
                </div>
                <div className="flex justify-between items-center">
                  <span>Compression:</span>
                  <Pill size="sm" variant="secondary">
                    {settings.compressionEnabled ? 'Enabled' : 'Disabled'}
                  </Pill>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span>Max Duration:</span>
                  <Pill size="sm" variant="secondary">{settings.maxSessionDuration}min</Pill>
                </div>
                <div className="flex justify-between items-center">
                  <span>Save Interval:</span>
                  <Pill size="sm" variant="secondary">{settings.autoSaveInterval}s</Pill>
                </div>
                <div className="flex justify-between items-center">
                  <span>Memory Limit:</span>
                  <Pill size="sm" variant="secondary">{settings.memoryThreshold}MB</Pill>
                </div>
              </div>
            </div>
          </GlassyCard>
        </div>

        <Separator />

        <div className="flex justify-between">
          <Pill
            variant="secondary"
            onClick={handleReset}
          >
            <RotateCcw className="w-4 h-4" strokeWidth={1.5} />
            Reset to Defaults
          </Pill>

          <div className="flex gap-2">
            <Pill
              variant="secondary"
              onClick={handleCancel}
            >
              <XCircle className="w-4 h-4" strokeWidth={1.5} />
              Cancel
            </Pill>
            <Pill
              variant="primary"
              onClick={handleSave}
              disabled={!hasChanges}
            >
              <CheckCircle className="w-4 h-4" strokeWidth={1.5} />
              Save Changes
            </Pill>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
